import React from 'react';
import {Divider} from '@mui/material';
import SwipeableDrawer, {
	type SwipeableDrawerProps,
} from '@mui/material/SwipeableDrawer';
import {
	type ActionMenuItemBaseProps,
	ActionMenuListItem,
	ActionMenuListItemAccordion,
	type ActionMenuListProps,
} from '@netx/core-components/ActionMenuList';
import {Button} from '@netx/core-components/Button';
import {MenuList} from '@netx/core-components/MenuList';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {i18n} from '@netx/core-next/locales/i18n-instance';
import {isElementOverflown} from '@netx/core-next/utils/element';
import {splitEventHandler} from '@netx/core-next/utils/fn';
import type {ReactAction} from '@netx/core/bootstrap';
import {ActionMenuHeader, type ActionMenuHeaderProps} from './ActionMenuHeader';

const ActionMenuMobileRoot = styled(SwipeableDrawer)(({theme}) => ({
	'& .MuiDrawer-paper': {
		borderRadius: '8px 8px 0px 0px',
		maxHeight: 'calc(100% - 32px)',
		display: 'flex',
		overflowY: 'hidden',
		flexDirection: 'column',
		'& .MuiDivider-root': {
			marginTop: 0,
			marginBottom: 0,
		},
		'& .ActionMenuHeader-root + .MuiList-root': {
			paddingTop: 0,
		},
	},
}));

const ActionMenuMobileList = styled(MenuList)(({theme}) => ({
	flex: '1 1 auto',
	overflowY: 'auto',
	overflowX: 'hidden',
	paddingBottom: 0,
}));

const ActionMenuMobileCancel = styled('div')(({theme}) => ({
	borderTop: `1px solid ${theme.palette.divider}`,
	flex: '0 0 auto',
	padding: 8,
}));

export interface ActionMenuMobileProps
	extends
		Omit<SwipeableDrawerProps, 'title'>,
		Pick<
			ActionMenuHeaderProps,
			'title' | 'secondaryTitle' | 'icon' | 'severity'
		>,
		Pick<ActionMenuListProps, 'actions' | 'disableIcons' | 'onAction'> {
	id?: string;
	slotProps?: SwipeableDrawerProps['slotProps'] & {
		header?: Partial<ActionMenuHeaderProps>;
	};
	ActionMenuHeader?: React.ReactElement | null;
}

export const renderAction = ({
	action,
	disableIcons,
	indentSize = 50,
	level = 0,
	someSelected,
	onAction,
	...props
}: ActionMenuItemBaseProps): React.ReactElement | null => {
	const {children, name, showChildWhenOnly, ...rest} = action;
	if (children?.length) {
		const flatChildren = children.flat();
		if (showChildWhenOnly && flatChildren.length === 1) {
			const _action = flatChildren[0];
			if (!_action) return null;
			return renderAction({
				action: _action,
				disableIcons,
				level,
				indentSize,
				onAction,
			});
		}

		const _someSelected = children.some((action) =>
			Array.isArray(action) ? false : action.selected,
		);
		return (
			<ActionMenuListItemAccordion
				key={`action-menu-item-accordion-${action.name}`}
				action={action}
				disableIcons={disableIcons}
				indentSize={indentSize}
				level={level}
				someSelected={_someSelected}
				renderAction={renderAction}
				onAction={onAction}
			/>
		);
	}

	return (
		<ActionMenuListItem
			key={`action-menu-item-${action.name}`}
			{...props}
			action={action}
			disableIcon={disableIcons}
			someSelected={someSelected}
			style={{paddingLeft: level ? level * indentSize : undefined}}
			onClick={(event) => onAction(event, action)}
		/>
	);
};

export const ActionMenuMobile = ({
	actions,
	disableIcons,
	icon,
	title,
	secondaryTitle,
	severity,
	slotProps,
	onAction,
	ActionMenuHeader: _ActionMenuHeader,
	...props
}: ActionMenuMobileProps) => {
	const menuListRef = React.useRef<HTMLUListElement>(null);

	const _renderAction = (action: ReactAction) =>
		renderAction({
			key: action.name,
			action,
			disableIcons,
			onAction,
		});

	return (
		<ActionMenuMobileRoot
			disableDiscovery
			disableSwipeToOpen
			anchor={'bottom'}
			open={props.open}
			{...props}
			// TODO: we need to stop propagation on the touch start event
			// to avoid our parent trigger from getting the ripple effect
			// but the listeners for this drawer are on the document - so we
			// can't just stop propagation on the drawer itself
			// need to investigate solutions...
			onClick={splitEventHandler(props.onClick, (event) => {
				event.stopPropagation();
			})}
		>
			{_ActionMenuHeader
				? _ActionMenuHeader
				: title != null && (
						<ActionMenuHeader
							icon={icon}
							title={title}
							secondaryTitle={secondaryTitle}
							severity={severity}
							{...slotProps?.header}
						/>
					)}

			<ActionMenuMobileList
				ref={menuListRef}
				onTouchStartCapture={(event) => {
					if (isElementOverflown(menuListRef.current, 'vertical'))
						event.stopPropagation();
				}}
			>
				{actions.map((action, index) => {
					return Array.isArray(action)
						? [
								index > 0 ? <Divider key={`divider-${index}`} /> : null,
								...action.map(_renderAction),
							]
						: _renderAction(action);
				})}
			</ActionMenuMobileList>

			<ActionMenuMobileCancel>
				<Button fullWidth variant="text" onClick={props.onClose}>
					{i18n.t('i18n.close')}
				</Button>
			</ActionMenuMobileCancel>
		</ActionMenuMobileRoot>
	);
};

export default ActionMenuMobile;
